import { useCallback, useMemo, useRef } from "react";

import { createPinForwarder } from "@app/util/map/createPinForwarder";
import { isPinEvent } from "@app/util/map/pinEventClaim";

type HoverHandler<T> = (item: T, event: google.maps.Data.MouseEvent) => void;

const HOVER_EVENTS = new Set(["mousemove", "mouseover"]);

export interface PinYieldHandlers<T> {
  onMouseLeave?: HoverHandler<T>;
  onMouseOver?: HoverHandler<T>;
}

// Deck pins draw above Data features but get no pointer events there, so an opted-in feature
// hands pin events to the map. Returns true when the feature must skip its own handler.
const usePinYield = <T>(map: google.maps.Map, enabled: boolean, handlers: PinYieldHandlers<T>) => {
  const forward = useMemo(() => createPinForwarder(map), [map]);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  // Set while the pointer is on a pin inside the feature: a Data pin used to end the hover there.
  const onPin = useRef(false);

  return useCallback(
    (eventName: string, event: google.maps.Data.MouseEvent, item: T): boolean => {
      if (!enabled) {
        return false;
      }
      forward(eventName, event);
      const isPinHit = isPinEvent(map, event);
      if (eventName === "mouseout") {
        // The hover already ended when the pointer reached the pin.
        const wasOnPin = onPin.current;
        onPin.current = false;
        return wasOnPin;
      }
      // Only pointer moves change the hover; a click follows a move that already did.
      if (!HOVER_EVENTS.has(eventName) || isPinHit === onPin.current) {
        return isPinHit;
      }
      onPin.current = isPinHit;
      // A mouseover onto a pin enters without hovering, and one off a pin runs the own onMouseOver.
      if (eventName === "mousemove") {
        const handler = isPinHit
          ? handlersRef.current.onMouseLeave
          : handlersRef.current.onMouseOver;
        handler?.(item, event);
      }
      return isPinHit;
    },
    [enabled, forward, map]
  );
};

export default usePinYield;
